import { Button, Card, Col, DatePicker, Form, FormInstance, Select, Row, Spin } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import axios from "axios";
import Papa from "papaparse";

const { Option } = Select;

interface EducationalBackgroundProps {
  form: FormInstance;
  setLoadingEdu: React.Dispatch<React.SetStateAction<boolean>>
}

interface FacultyProgram {
  id: string;
  faculty: string;
  level: string;
  program: string;
}



const EducationalBackgroundForm: React.FC<EducationalBackgroundProps> = ({ setLoadingEdu, form }) => {
  const [csvData, setCsvData] = useState<FacultyProgram[] | null>(null);
  const [institutions, setInstitutions] = useState<string[] | null>(null);
  const [selectedData, setSelectedData] = useState<{ [key: number]: { institution?: string; level?: string; faculty?: string } }>({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingEdu(true);
        // Fetch faculty/program data
        const facultyResponse = await axios.get(
          "https://docs.google.com/spreadsheets/d/e/2PACX-1vQCS6B8nevSz8WNrCJG1CPZbYHeyWayBwAEpEOLx58M-2lRHW0qmLo5wa9KobPAXRoX3HCKRLel-Zra/pub?gid=1009883756&single=true&output=csv"
        );
        const parsedFacultyData = parseFacultyCSV(facultyResponse.data);
        setCsvData(parsedFacultyData);

        // Fetch institutions data
        const institutionsResponse = await axios.get(
          "https://docs.google.com/spreadsheets/d/e/2PACX-1vSS6vUxja_GbaI6PK6X2RiLoONr1dhFKRjCQH0LpgyFbue1rANYX38NV2OAVGfvac3iJHqLh4115b4j/pub?gid=1817478697&single=true&output=csv"
        );
        const parsedInstitutions = parseInstitutionsCSV(institutionsResponse.data);
        setInstitutions(parsedInstitutions);
      } catch (error) {
        setLoadingEdu(false);
        console.error("Error fetching data:", error);
      }
      setLoadingEdu(false)
    };

    const parseFacultyCSV = (csvText: string): FacultyProgram[] => {
      const { data } = Papa.parse<string[]>(csvText, { skipEmptyLines: true });
      return data.slice(1)
        .filter((row) => row.length === 4 && row.every((val) => val?.trim()))
        .map(([id, faculty, level, program]) => ({ id, faculty, level, program }));
    };

    const parseInstitutionsCSV = (csvText: string): string[] => {
      const { data } = Papa.parse<string[]>(csvText, { skipEmptyLines: true });
      const institutions = new Set<string>();
      data.slice(1).forEach((row) => {
        const name = row[1]?.trim();
        if (name) institutions.add(name);
      });
      return [...institutions, "Others"];
    };

    fetchData();
  }, [setLoadingEdu]);

  if (!csvData || !institutions) {
    return <Spin />
  };

  const uniqueLevels = Array.from(new Set(csvData.map((item) => item.level)));


  return (
    <Form.List name="educationalBackground" initialValue={[{}]} >
      {(fields, { add, remove }) => (
        <div style={{ display: "flex", rowGap: 16, flexDirection: "column" }}>
          {fields.map((field) => {
            const selectedInstitution = selectedData[field.name]?.institution;
            const selectedLevel = selectedData[field.name]?.level;
            const selectedFaculty = selectedData[field.name]?.faculty;

            const uniqueFaculties = Array.from(new Set(
              csvData.filter((item) => item.level === selectedLevel).map((item) => item.faculty)
            ));

            const uniquePrograms = Array.from(new Set(
              csvData.filter((item) => item.level === selectedLevel && item.faculty === selectedFaculty).map((item) => item.program)
            ));

            return (
              <Card
                size="small"
                title={`Education ${field.name + 1}`}
                key={field.key}
                extra={fields.length > 1 && (
                  <CloseOutlined onClick={() => remove(field.name)} />
                )}
              >
                <Row gutter={[48, 32]}>
                  {/* Institution Selection */}
                  <Col xs={24} sm={24} md={24} lg={12} xl={12}>
                    <Form.Item name={[field.name, "institutionName"]} label="School / University">
                      <Select
                        showSearch
                        placeholder="Select Institution"
                        onChange={(value) => {
                          setSelectedData((prev) => ({
                            ...prev,
                            [field.name]: { institution: value },
                          }));
                          form.setFieldsValue({
                            educationalBackground: {
                              [field.name]: { degree: undefined, faculty: undefined, fieldOfStudy: undefined },
                            },
                          });
                        }}
                      >
                        {institutions.map((institution) => (
                          <Option key={institution} value={institution}>
                            {institution}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  {/* Level Selection (Disabled until Institution is selected) */}
                  <Col xs={24} sm={24} md={24} lg={12} xl={12}>
                    <Form.Item name={[field.name, "degree"]} label="Level" rules={[{ required: true }]}>
                      <Select
                        showSearch
                        placeholder="Select Level"
                        disabled={!selectedInstitution}
                        onChange={(value) => {
                          setSelectedData((prev) => ({
                            ...prev,
                            [field.name]: { ...prev[field.name], level: value },
                          }));
                          form.setFieldsValue({
                            educationalBackground: {
                              [field.name]: { faculty: undefined, fieldOfStudy: undefined },
                            },
                          });
                        }}
                      >
                        {uniqueLevels.map((level) => (
                          <Option key={level} value={level}>
                            {level}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  {/* Faculty Selection (Disabled until Level is selected) */}
                  <Col xs={24} sm={24} md={24} lg={12} xl={12}>
                    <Form.Item name={[field.name, "faculty"]} label="Faculty" rules={[{ required: true }]}>
                      <Select
                        showSearch
                        placeholder="Select Faculty"
                        disabled={!selectedLevel}
                        onChange={(value) => {
                          setSelectedData((prev) => ({
                            ...prev,
                            [field.name]: { ...prev[field.name], faculty: value },
                          }));
                          form.setFieldsValue({
                            educationalBackground: {
                              [field.name]: { fieldOfStudy: undefined },
                            },
                          });
                        }}
                      >
                        {uniqueFaculties.map((faculty) => (
                          <Option key={faculty} value={faculty}>
                            {faculty}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  {/* Program Selection (Disabled until Faculty is selected) */}
                  <Col xs={24} sm={24} md={24} lg={12} xl={12}>
                    <Form.Item name={[field.name, "fieldOfStudy"]} label="Program" rules={[{ required: true }]}>
                      <Select showSearch placeholder="Select Program" disabled={!selectedFaculty}>
                        {uniquePrograms.map((program) => (
                          <Option key={program} value={program}>
                            {program}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  {/* Start & End Dates */}
                  <Col xs={24} sm={24} md={24} lg={12} xl={12}>
                    <Form.Item name={[field.name, "startDate"]} label="Start Date" rules={[{ required: true }]}>
                      <DatePicker className="w-full" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={12} xl={12}>
                    <Form.Item name={[field.name, "endDate"]} label="End Date (Expected)" rules={[{ required: true }]}>
                      <DatePicker
                        className="w-full"
                        disabledDate={(current) => {
                          const startDate = form.getFieldValue(["educationalBackground", field.name, "startDate"]);
                          if (!startDate) return false;
                          return current && current.isBefore(startDate, "day");
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            );
          })}
          {fields.length < 6 && (
            <Button type="dashed" onClick={() => add()} block>
              + Add more
            </Button>
          )}

        </div>
      )}
    </Form.List>
  );
};

export default EducationalBackgroundForm;
